import { JSX, useState, useTransition } from 'react';

import { Text } from '@videoblocks/design-system';

import CreditConfirmationModal from '../_components/CreditConfirmationModal';
import GeneratedVoicePlayer from '../_components/GeneratedVoicePlayer';
import Modal from '../_components/Modal';
import TextInput from '../_components/TextInput';
import UploadInput from '../_components/UploadInput';
import { useVoiceoverContext } from '../_context/VoiceoverContext';
import { TAB_TO_EVENT_TYPE, Tabs, initialCloneMyVoice, useVoiceoverTabContext } from '../_context/VoiceoverTabContext';

export const UploadCloneStep = ({ setError }: { setError: (err: string) => void }): JSX.Element => {
    const [isPending, startTransition] = useTransition();
    const { updateState } = useVoiceoverTabContext();
    const { user, accountCode } = useVoiceoverContext();

    const handleVoiceCloning = (file: File) => {
        startTransition(async () => {
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('userId', String(user?.id || ''));
                formData.append('accountCode', accountCode || '');
                const response = await fetch('/api/voiceover/create-voice', {
                    method: 'POST',
                    body: formData,
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // i.e. VoiceCloneResponse from 'ml-text-service-client'
                const { results } = (await response.json()) as { results: { voice_id: string } };
                updateState({
                    generatedVoiceId: results.voice_id,
                    userAudioPreviewUrl: URL.createObjectURL(file),
                    isUpload: false,
                });
            } catch (error) {
                setError('Voiceover service is temporarily unavailable. Please try again later.');
            }
        });
    };

    return (
        <div className="text-center">
            <UploadInput
                handleFileUpload={handleVoiceCloning}
                isPending={isPending}
                title="Upload a short recording to create your personal AI voice"
            />
        </div>
    );
};

export const CloneToTextStep = ({
    userAudioPreviewUrl,
    generatedVoiceId,
}: {
    userAudioPreviewUrl: string;
    generatedVoiceId: string;
}): JSX.Element => {
    const { user, accountCode } = useVoiceoverContext();

    const {
        updateState,
        playingVoiceId,
        cloneMyVoice: {
            script,
            generatedAudioUrl,
            userAudioHasPlayed,
            generatedAudioHasPlayed,
            generatedAudioIsComplete,
            generatedAudioIsFetching,
            calculatedCost,
            voiceoverParams: { speed },
            aiContentId,
            lastAiContentId,
        },
    } = useVoiceoverTabContext();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleDeleteAudio = () => {
        updateState(initialCloneMyVoice);
        handleCloseModal();
    };

    return (
        <>
            <CreditConfirmationModal
                isOpen={confirmModalOpen}
                setIsOpen={setConfirmModalOpen}
                creditCost={calculatedCost}
                buttonText="Generate Voiceover"
                generateRequestBody={() =>
                    JSON.stringify({
                        text: script,
                        voiceId: generatedVoiceId,
                        speed: (speed as number) / 100,
                        userId: user!.id,
                        accountCode: accountCode,
                    })
                }
            />
            <div className="text-center">
                <TextInput
                    playingVoiceId={playingVoiceId}
                    script={script ?? ''}
                    speed={speed as number}
                    onSubmit={() => setConfirmModalOpen(true)}
                    buttonText={generatedAudioUrl ? 'Regenerate Voiceover' : 'Generate Voiceover'}
                    voiceId={generatedVoiceId}
                    calculatedCost={calculatedCost}
                    placeholder="Preview your audio below. Then, enter your script or text to generate a voiceover."
                    submitDisabled={!generatedAudioIsComplete}
                />
                <div className="flex flex-col sm:flex-row sm:gap-4">
                    <GeneratedVoicePlayer
                        audioUrl={userAudioPreviewUrl}
                        hasPlayed={userAudioHasPlayed}
                        setHasPlayed={() => updateState({ userAudioHasPlayed: true })}
                        title="Uploaded Audio"
                        onDelete={() => {
                            handleOpenModal();
                        }}
                    />
                    {(generatedAudioUrl || generatedAudioIsFetching) && (
                        <GeneratedVoicePlayer
                            audioUrl={generatedAudioUrl ?? ''}
                            isComplete={generatedAudioIsComplete}
                            isFetching={generatedAudioIsFetching}
                            title="Generated Voiceover"
                            hasPlayed={generatedAudioHasPlayed}
                            setHasPlayed={() => updateState({ generatedAudioHasPlayed: true })}
                            aiContentId={aiContentId}
                            voiceId={generatedVoiceId}
                            aiContentVoiceoverType={TAB_TO_EVENT_TYPE[Tabs.CloneMyVoice]}
                            lastAiContentId={lastAiContentId}
                            script={script}
                        />
                    )}
                </div>
                <Modal
                    title="Delete audio and voiceover?"
                    body={
                        <>
                            <Text fontWeight="regular">
                                Removing your uploaded audio will also delete your AI-generated voiceover.
                            </Text>
                            <Text fontWeight="regular">
                                If you continue, you&apos;ll have to restart the Clone my Voice process.
                            </Text>
                            <Text fontWeight="regular">This action is permanent and cannot be undone.</Text>
                        </>
                    }
                    primaryActionText="Delete uploaded audio"
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onConfirm={handleDeleteAudio}
                />
            </div>
        </>
    );
};

const CloneVoiceToSpeech = ({ setError }: { setError: (err: string) => void }): JSX.Element => {
    const { cloneMyVoice: tabState } = useVoiceoverTabContext();

    if (tabState.isUpload) {
        return <UploadCloneStep setError={setError} />;
    }

    return (
        <CloneToTextStep
            userAudioPreviewUrl={tabState.userAudioPreviewUrl!}
            generatedVoiceId={tabState.generatedVoiceId!}
        />
    );
};

export default CloneVoiceToSpeech;
